import React, { useState } from 'react';
import { Box, Typography, Paper, Collapse, IconButton } from '@mui/material';
import { DateTime } from 'luxon';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const times = [
    { label: 'Morning', start: 6, end: 9 },
    { label: 'Midday', start: 10, end: 12 },
    { label: 'Afternoon', start: 13, end: 16 },
    { label: 'Evening', start: 17, end: 20 },
    { label: 'Night', start: 21, end: 23 },
];

const HeatmapMatrix = ({ city, dataMatrix, timezone, intensityColor }) => {
    const [expandedDay, setExpandedDay] = useState(null);

    const handleExpandClick = (day) => {
        setExpandedDay(expandedDay === day ? null : day);
    };

    const formatRange = (startHour, endHour) => {
        const start = DateTime.fromObject({ hour: startHour }, { zone: timezone });
        const end = DateTime.fromObject({ hour: endHour }, { zone: timezone });
        return `${start.toFormat('h:mm a')} - ${end.toFormat('h:mm a')}`;
    };

    // --- Desktop Grid Layout (remains unchanged) ---
    const DesktopGrid = (
        <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: `120px repeat(${days.length}, 1fr)`, gap: 1 }}>
            <Box />
            {days.map((day) => <Typography key={day} sx={{ color: '#fff', textAlign: 'center', fontSize: '0.9rem', fontWeight: 600 }}>{day}</Typography>)}
            {times.map(({ label, start, end }) => (
                <React.Fragment key={label}>
                    <Box>
                        <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>{label}</Typography>
                        <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>{formatRange(start, end)}</Typography>
                    </Box>
                    {days.map((day) => {
                        const key = `${day}-${label}`;
                        const count = dataMatrix[city]?.[key] || 0;
                        return (
                            <Box key={key} sx={{ height: 42, borderRadius: '6px', backgroundColor: intensityColor(count), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 'bold' }}>{count}</Typography>
                            </Box>
                        );
                    })}
                </React.Fragment>
            ))}
        </Box>
    );

    // ✅ FIX: Mobile list is now more compact ---
    const MobileList = (
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {days.map((day) => {
                const isExpanded = expandedDay === day;
                return (
                    <Box key={day} sx={{ borderBottom: '1px solid #3a3a3a' }}>
                        {/* Clickable Header */}
                        <Box
                            onClick={() => handleExpandClick(day)}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 1, // Reduced padding
                                cursor: 'pointer',
                            }}
                        >
                            <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}> {/* Changed to body1 */}
                                {day}
                            </Typography>
                            <IconButton size="small" sx={{ color: '#fff' }}> {/* Made icon smaller */}
                                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                        </Box>

                        {/* Collapsible Content */}
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ pl: 2, pr: 1, pb: 1 }}> {/* Adjusted padding */}
                                {times.map(({ label, start, end }) => {
                                    const key = `${day}-${label}`;
                                    const count = dataMatrix[city]?.[key] || 0;
                                    return (
                                        <Box
                                            key={key}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                py: 0.5, // Reduced vertical padding
                                            }}
                                        >
                                            <Box>
                                                <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>{label}</Typography>
                                                <Typography sx={{ color: '#888', fontSize: '0.7rem' }}>{formatRange(start, end)}</Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    height: 28, // Smaller height
                                                    width: 28,  // Smaller width
                                                    borderRadius: '6px', // Changed to rounded square
                                                    backgroundColor: intensityColor(count),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Typography sx={{ color: '#fff', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                    {count}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Collapse>
                    </Box>
                );
            })}
        </Box>
    );

    return (
        <Paper elevation={3} sx={{ backgroundColor: '#2a2a2a', p: { xs: 1, sm: 3 } }}> {/* Reduced mobile padding */}
            {DesktopGrid}
            {MobileList}
        </Paper>
    );
};

export default HeatmapMatrix;
